import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { BillingErrorsValues } from 'lib.components/src/components/BillingForm/models';
import {
  initialBillingErrorsValues,
  initialBillingValues,
} from 'lib.components/src/components/BillingForm/constants';
import { showApiError } from 'lib.shared/src/handleApiError';
import { usePatchBillingInfoMutation } from 'lib.store/src/main/billing/billing.api';
import {
  PatchBillingInfoBodyRequest,
  UserBillingFullInfo,
} from 'lib.store/src/main/billing/models';
import { IntentResponse } from 'lib.store/src/main/payment/models';
import { userActions } from 'lib.store/src/main/-user/user.slice';
import { useDispatch } from 'react-redux';
import sendAnalytics from 'lib.hooks/src/sendAnalytics/sendAnalytics';
import { GTMEventName } from 'lib.hooks/src/sendAnalytics/constants';
import { notification } from 'antd';
import { useAppSelector } from 'lib.hooks/src/redux';
import { billingActions } from 'lib.store/src/main/billing/billing.slice';
import { UserType } from 'lib.store/src/models-to-replace/auth';
import type { RootState } from 'lib.store/src/index';
import { useSearchParams, useLocation } from 'react-router-dom';
import { BillingSectionType } from './models/billing-section-types';
import { PaymentMethodItem } from 'lib.store/src/models-to-replace/paymentMethods';
import { useGetUserBillingPaymentsQuery } from 'lib.store/src/main/payment/payment.api';

interface UserBillingPageContextType {
  paymentIdToEdit: string;
  setPaymentIdToEdit: (id: string) => void;
  close: () => void;
  validationErrors: BillingErrorsValues;
  setValidationErrors: (errors: BillingErrorsValues) => void;
  isLoading: boolean;
  isUpdating: boolean;
  setUpdating: (isUpdating: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  form: PatchBillingInfoBodyRequest;
  setForm: (values: PatchBillingInfoBodyRequest) => void;
  drawerForm: PatchBillingInfoBodyRequest;
  setDrawerForm: (values: PatchBillingInfoBodyRequest) => void;
  onFormChange: (values: Partial<PatchBillingInfoBodyRequest>) => void;
  onBillingInfoUpdate: (redirectUrl?: string, callback?: () => void) => void;

  stripeData: IntentResponse | null;
  setStripeData: (stripeData: IntentResponse | null) => void;

  selectedPlanId: number | null;
  setSelectedPlanId: (planId: number | null) => void;

  clickedSectionType: BillingSectionType;
  setClickedSectionType: (sectionType: BillingSectionType) => void;

  actualSectionType: BillingSectionType;
  setActualSectionType: (sectionType: BillingSectionType) => void;

  topUpAmount: number | null;
  setTopUpAmount: (topUpAmount: number | null) => void;

  isModalView: boolean;
  setIsModalView: (isModalView: boolean) => void;

  cards: PaymentMethodItem[];
  setCards: (cards: PaymentMethodItem[]) => void;

  actualDrawerTypeHandler: ({
    actionType,
    isModalView,
  }: {
    actionType: BillingSectionType;
    isModalView: boolean;
  }) => void;
}

export const UserBillingPageContext = createContext<UserBillingPageContextType>({
  paymentIdToEdit: '',
  setPaymentIdToEdit: () => {},
  close: () => {},
  validationErrors: initialBillingErrorsValues,
  setValidationErrors: () => {},
  isLoading: false,
  setLoading: () => {},
  isUpdating: false,
  setUpdating: () => {},
  form: initialBillingValues,
  setForm: () => {},
  drawerForm: initialBillingValues,
  setDrawerForm: () => {},
  onFormChange: () => {},
  onBillingInfoUpdate: () => {},

  stripeData: null,
  setStripeData: () => {},

  selectedPlanId: null,
  setSelectedPlanId: () => {},

  topUpAmount: null,
  setTopUpAmount: () => {},

  clickedSectionType: BillingSectionType.NONE,
  setClickedSectionType: () => {},

  actualSectionType: BillingSectionType.NONE,
  setActualSectionType: () => {},

  isModalView: false,
  setIsModalView: () => {},

  cards: [],
  setCards: () => {},

  actualDrawerTypeHandler: (_args: { actionType: BillingSectionType; isModalView: boolean }) => {},
});

interface UserBillingPageProviderProps {
  children: ReactNode;
}

export const UserBillingPageProvider: React.FC<UserBillingPageProviderProps> = ({ children }) => {
  const { user: fetchedUser } = useAuth();
  const userState = useAppSelector((state: RootState) => state.userAuth.user);
  const user = userState || fetchedUser;
  const dispatch = useDispatch();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const [patchBillingInfo] = usePatchBillingInfoMutation();

  const [paymentIdToEdit, setPaymentIdToEdit] = useState('');

  const billingFlowMode = useAppSelector((state: RootState) => state.billingState.flowMode);
  const [form, setForm] = useState<PatchBillingInfoBodyRequest>(initialBillingValues);
  const [drawerForm, setDrawerForm] = useState<PatchBillingInfoBodyRequest>(initialBillingValues);
  const [validationErrors, setValidationErrors] = useState<BillingErrorsValues>(
    initialBillingErrorsValues as BillingErrorsValues,
  );
  const [isLoading, setLoading] = useState(false);
  const [isUpdating, setUpdating] = useState(false);
  const [stripeData, setStripeData] = useState<IntentResponse | null>(null);
  const [cards, setCards] = useState<PaymentMethodItem[]>([]);
  const [isModalView, setIsModalView] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [clickedSectionType, setClickedSectionType] = useState<BillingSectionType>(
    BillingSectionType.NONE,
  );
  const [actualSectionType, setActualSectionType] = useState<BillingSectionType>(
    BillingSectionType.NONE,
  );
  const [topUpAmount, setTopUpAmount] = useState<number | null>(null);

  const { data: paymentMethodsResponse } = useGetUserBillingPaymentsQuery();

  useEffect(() => {
    if (paymentMethodsResponse?.data) {
      setCards(paymentMethodsResponse.data);
    }
  }, [paymentMethodsResponse]);

  const onFormChange = (value: Partial<PatchBillingInfoBodyRequest>) => {
    setValidationErrors(initialBillingErrorsValues);
    setDrawerForm((prev) => ({ ...prev, ...value })); // Only update drawer form
  };

  const close = () => {
    if (searchParams.has('billing-setup')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('billing-setup');
      setSearchParams(nextParams);
    }
    dispatch(billingActions.resetBillingDrawerType());
    setPaymentIdToEdit('');
    setSelectedPlanId(null);
    setClickedSectionType(BillingSectionType.NONE);
    setActualSectionType(BillingSectionType.NONE);
    setIsModalView(false);
    setValidationErrors(initialBillingErrorsValues as BillingErrorsValues);
    setTopUpAmount(null);
  };

  const onBillingInfoUpdate = async (_redirectUrl?: string, callback?: () => void) => {
    setUpdating(true);
    try {
      const response = await patchBillingInfo(drawerForm);
      if ('data' in response && response.data) {
        sendAnalytics(GTMEventName.addBillingInfo);
        setLoading(false);
        dispatch(
          billingActions.setBillingInfoUser(response.data.data as unknown as UserBillingFullInfo),
        );
        const billingId =
          response.data.data?.organization?.billing?.id || user?.organization?.billing?.id;

        // Update the display form state after successful submission
        setForm(drawerForm);

        const newUserData = {
          ...user,
          name: drawerForm.name,
          email: drawerForm.email,
          organization: {
            ...user?.organization,
            billing: {
              ...user?.organization?.billing,
              id: billingId,
              email: drawerForm.email,
              company_name: drawerForm.company_name,
              address: drawerForm.address,
              tax_code: drawerForm.tax_code,
              postal_code: drawerForm.postal_code,
              city: drawerForm.city,
              country: drawerForm.country,
            },
          },
        };
        dispatch(userActions.patchUserState(newUserData as UserType));
        if (clickedSectionType !== BillingSectionType.TOP_UP) {
          setTopUpAmount(null);
        }
        // Only clear query param when it exists, and do not force a change otherwise
        if (searchParams.has('billing-setup')) {
          searchParams.delete('billing-setup');
          setSearchParams(searchParams);
        }

        notification.open({
          message: 'Your changes to the billing information have been saved successfully',
          placement: 'bottomLeft',
          closeIcon: false,
          type: 'success',
        });

        if (callback) {
          return callback();
        }
        if (
          billingFlowMode === 'add_card' ||
          billingFlowMode === 'change_plan' ||
          billingFlowMode === 'buy_credits'
        ) {
          //dp???
          return dispatch(billingActions.setBillingDrawerType('add_payment_method'));
        }
        return close();
      }
      if ('error' in response) {
        const errors = showApiError(response, 'Billing update error');
        setValidationErrors(errors as unknown as BillingErrorsValues);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    if (
      searchParams.has('billing-setup') &&
      actualSectionType !== BillingSectionType.BILLING_INFO
    ) {
      setClickedSectionType(BillingSectionType.BILLING_INFO);
      setActualSectionType(BillingSectionType.BILLING_INFO);
    }
  }, [searchParams, actualSectionType, setClickedSectionType, setActualSectionType]);

  useEffect(() => {
    if (user) {
      const userData = {
        name: user?.name || '',
        email: user?.organization?.billing?.email || user?.email || '',
        company_name: user?.organization?.billing?.company_name || '',
        address: user?.organization?.billing?.address || '',
        tax_code: user?.organization?.billing?.tax_code || '',
        postal_code: user?.organization?.billing?.postal_code || '',
        city: user?.organization?.billing?.city || null,
        country: user?.organization?.billing?.country || null,
      };
      setForm((prev) => ({ ...prev, ...userData }));
      setDrawerForm((prev) => ({ ...prev, ...userData }));
    }
  }, [user]);

  useEffect(() => {
    if (clickedSectionType === BillingSectionType.BILLING_INFO) {
      setDrawerForm(form);
    }
  }, [clickedSectionType, form]);

  useEffect(() => {
    // Do not reset drawer state when explicitly requested via query param
    if (searchParams.has('billing-setup')) {
      return;
    }
    // Avoid resetting while a flow is in progress
    if (
      clickedSectionType !== BillingSectionType.NONE ||
      actualSectionType !== BillingSectionType.NONE
    ) {
      return;
    }
    setClickedSectionType(BillingSectionType.NONE);
    setActualSectionType(BillingSectionType.NONE);
  }, [location.pathname, searchParams, clickedSectionType, actualSectionType]);

  const actualDrawerTypeHandler = ({
    actionType,
    isModalView,
  }: {
    actionType: BillingSectionType;
    isModalView: boolean;
  }) => {
    if (!user?.organization?.billing?.id) {
      if (isModalView) {
        setIsModalView(true);
      }
      setClickedSectionType(actionType);
      setActualSectionType(BillingSectionType.BILLING_INFO);
      return;
    }
    if (Array.isArray(cards) && cards.length === 0) {
      if (isModalView) {
        setIsModalView(true);
      }
      setClickedSectionType(actionType);
      setActualSectionType(BillingSectionType.ADD_PAYMENT_METHOD);
      return;
    } else {
      setClickedSectionType(actionType);
      if (
        actionType === BillingSectionType.ADD_PAYMENT_METHOD ||
        actionType === BillingSectionType.BILLING_INFO
      ) {
        setActualSectionType(actionType);
      } else {
        setActualSectionType(BillingSectionType.NONE);
      }
    }
  };

  return (
    <UserBillingPageContext.Provider
      value={{
        paymentIdToEdit,
        setPaymentIdToEdit,
        close,
        isLoading,
        setLoading,
        isUpdating,
        setUpdating,
        validationErrors,
        setValidationErrors,
        form,
        setForm,
        drawerForm,
        setDrawerForm,
        onFormChange,
        onBillingInfoUpdate,
        stripeData,
        setStripeData,

        selectedPlanId,
        setSelectedPlanId,

        topUpAmount,
        setTopUpAmount,

        clickedSectionType,
        setClickedSectionType,

        actualSectionType,
        setActualSectionType,

        isModalView,
        setIsModalView,

        cards,
        setCards,

        actualDrawerTypeHandler,
      }}
    >
      {children}
    </UserBillingPageContext.Provider>
  );
};
